import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Box, Button, Divider, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import Tab from '@mui/material/Tab';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import { useField } from 'formik';
import DecimalTextField from './FieldTypes/DecimalField';

interface DevFeeAdjModalProps {
  show: boolean;
  onHide: () => void;
  isViewOnly: boolean;
  projectType?: string;
}

interface DevFeeRowProps {
  dNum: 1 | 2 | 3;
  prefix: 'pv' | 'bess';
  isViewOnly: boolean;
}

const DevFeeRow: React.FC<DevFeeRowProps> = ({ dNum, prefix, isViewOnly }) => {
  const [grsField] = useField(`sr_finl_dev_fee_inv_${prefix}_d_${dNum}_grs_nom`);
  const [adjField] = useField(`sr_finl_dev_fee_inv_${prefix}_d_${dNum}_adj_nom`);

  const grsNum =
    grsField.value != null && grsField.value !== ''
      ? parseFloat(grsField.value)
      : null;
  const adjNum =
    adjField.value != null && adjField.value !== ''
      ? parseFloat(adjField.value)
      : 0;

  const grsDisplay = grsNum !== null ? grsNum.toFixed(2) : '';
  const netValue = grsNum !== null ? (grsNum + adjNum).toFixed(2) : '';

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
        Draw {dNum}
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={4}>
          <TextField
            label="Gross"
            value={grsDisplay}
            disabled
            fullWidth
            margin="dense"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            InputLabelProps={{ sx: { fontWeight: 'bold' } }}
          />
        </Grid>
        <Grid item xs={4}>
          <DecimalTextField
            name={`sr_finl_dev_fee_inv_${prefix}_d_${dNum}_adj_nom`}
            label="Adjustment"
            decimals={2}
            isTouched={false}
            disabled={isViewOnly}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Net"
            value={netValue}
            disabled
            fullWidth
            margin="dense"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            InputLabelProps={{ sx: { fontWeight: 'bold' } }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const DevFeeAdjModal: React.FC<DevFeeAdjModalProps> = ({ show, onHide, isViewOnly, projectType }) => {
  const showPv = projectType !== 'Storage-only';
  const showBess = projectType !== 'Solar-only';
  const [tab, setTab] = useState(showPv ? 'pv' : 'bess');

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Dev Fee Adjustment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <TabContext value={tab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={(_, v) => setTab(v)}>
              {showPv && <Tab value="pv" label="PV" />}
              {showBess && <Tab value="bess" label="BESS" />}
            </TabList>
          </Box>
          {showPv && (
            <TabPanel value="pv">
              <DevFeeRow dNum={1} prefix="pv" isViewOnly={isViewOnly} />
              <Divider sx={{ my: 2 }} />
              <DevFeeRow dNum={2} prefix="pv" isViewOnly={isViewOnly} />
              <Divider sx={{ my: 2 }} />
              <DevFeeRow dNum={3} prefix="pv" isViewOnly={isViewOnly} />
            </TabPanel>
          )}
          {showBess && (
            <TabPanel value="bess">
              <DevFeeRow dNum={1} prefix="bess" isViewOnly={isViewOnly} />
              <Divider sx={{ my: 2 }} />
              <DevFeeRow dNum={2} prefix="bess" isViewOnly={isViewOnly} />
              <Divider sx={{ my: 2 }} />
              <DevFeeRow dNum={3} prefix="bess" isViewOnly={isViewOnly} />
            </TabPanel>
          )}
        </TabContext>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="contained" onClick={onHide} style={{ color: 'white' }}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DevFeeAdjModal;
